import { InputSeries } from '../api/cppmodelApi';
import { getSignalSeries, valueAt } from './SimulationGraph';
import type { ApiGraphData } from './SimulationResults';

// Drop binary floating point noise from the subtraction.
const clean = (value: number): number => Number(value.toPrecision(12));

// current − previous of one signal, change-compressed like the series it
// comes from: a point at every time either run changed, kept only where the
// difference changes, plus the end time. Only the time both runs cover is
// compared, so a longer run doesn't read as a difference. Null when the runs
// don't overlap.
export const diffSeries = (current: InputSeries, previous: InputSeries): InputSeries | null => {
    if (current.x.length === 0 || previous.x.length === 0) {
        return null;
    }

    const start = Math.max(current.x[0], previous.x[0]);
    const end = Math.min(current.x[current.x.length - 1], previous.x[previous.x.length - 1]);
    const times = Array.from(new Set([start, ...current.x, ...previous.x, end]))
        .filter((time) => time >= start && time <= end)
        .sort((a, b) => a - b);
    const x: number[] = [];
    const y: number[] = [];

    times.forEach((time, index) => {
        const difference = clean((valueAt(current, time) ?? 0) - (valueAt(previous, time) ?? 0));

        if (index === 0 || index === times.length - 1 || difference !== y[y.length - 1]) {
            x.push(time);
            y.push(difference);
        }
    });

    return { label: current.label, x, y };
};

// The largest |current − previous| of a signal over the time both runs cover.
export const maxAbsDifference = (difference: InputSeries): number => (
    Math.max(0, ...difference.y.map(Math.abs))
);

// Legend name -> current − previous, for the signals both runs have.
export const getSignalDifferences = (current: ApiGraphData, previous: ApiGraphData): Map<string, InputSeries> => {
    const previousSeries = getSignalSeries(previous);
    const differences = new Map<string, InputSeries>();

    getSignalSeries(current).forEach((series, name) => {
        const other = previousSeries.get(name);
        const difference = other ? diffSeries(series, other) : null;

        if (difference) {
            differences.set(name, difference);
        }
    });

    return differences;
};

// A run to graph whose signals are current − previous. Signals only one run
// has are left out; the CppModel.* outputs of the current run stay, so the
// step strip still shows its verdicts.
export const buildDifferenceData = (current: ApiGraphData, differences: Map<string, InputSeries>): ApiGraphData => ({
    ...current,
    inputs: current.inputs.flatMap((input) => differences.get(`In: ${input.label}`) ?? []),
    results: current.results.flatMap((result) => (
        result.label.includes('CppModel.') ? result : differences.get(`Out: ${result.label}`) ?? []
    )),
});
